import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Modal, LoadingSpinner } from './ui';
import { incidentReportsAPI } from '../services/api';

type Category = 'ERROR' | 'INCIDENT' | 'SUPPORT';

export interface IncidentReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialCategory?: Category;
}

const categories: { id: Category; label: string; hint: string }[] = [
  { id: 'ERROR', label: 'Reportar error', hint: 'Fallos, pantallas en blanco, datos incorrectos' },
  { id: 'INCIDENT', label: 'Reportar incidencia', hint: 'Lentitud, comportamiento extraño' },
  { id: 'SUPPORT', label: 'Ayuda / soporte', hint: 'Dudas de uso o mejora' },
];

const IncidentReportModal: React.FC<IncidentReportModalProps> = ({
  isOpen,
  onClose,
  initialCategory = 'SUPPORT',
}) => {
  const location = useLocation();
  const [category, setCategory] = useState<Category>(initialCategory);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [module, setModule] = useState('other');
  const [severity, setSeverity] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'>('MEDIUM');
  const [reproductionSteps, setReproductionSteps] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [stackTrace, setStackTrace] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const { data: metaRes, isLoading: metaLoading } = useQuery(
    ['incident-reports-meta'],
    () => incidentReportsAPI.getMeta(),
    { enabled: isOpen, staleTime: 60 * 60 * 1000 }
  );

  const suggested =
    (metaRes?.data?.data?.suggestedModules as { value: string; label: string }[] | undefined) ||
    [];

  const moduleOptions = useMemo(() => {
    if (suggested.length) return suggested;
    return [{ value: 'other', label: 'Otro / general' }];
  }, [suggested]);

  useEffect(() => {
    if (isOpen) {
      setCategory(initialCategory);
    }
  }, [isOpen, initialCategory]);

  const reset = () => {
    setTitle('');
    setDescription('');
    setModule('other');
    setSeverity('MEDIUM');
    setReproductionSteps('');
    setErrorMessage('');
    setStackTrace('');
    setFile(null);
  };

  const createMutation = useMutation(
    () => {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('description', description.trim());
      fd.append('module', module.trim());
      fd.append('severity', severity);
      fd.append('category', category);
      if (reproductionSteps.trim()) fd.append('reproductionSteps', reproductionSteps.trim());
      fd.append('pageUrl', `${window.location.origin}${location.pathname}${location.search || ''}`);
      fd.append('browserInfo', typeof navigator !== 'undefined' ? navigator.userAgent : '');
      if (errorMessage.trim()) fd.append('errorMessage', errorMessage.trim());
      if (stackTrace.trim()) fd.append('stackTrace', stackTrace.trim());
      if (file) fd.append('attachment', file);
      return incidentReportsAPI.create(fd);
    },
    {
      onSuccess: (res) => {
        toast.success(res?.data?.message || 'Reporte enviado');
        reset();
        onClose();
      },
      onError: (err: any) => {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          'No se pudo enviar el reporte';
        toast.error(msg);
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (title.trim().length < 3) {
      toast.error('Indica un título más descriptivo');
      return;
    }
    if (description.trim().length < 10) {
      toast.error('Describe el problema con al menos unas pocas palabras');
      return;
    }
    createMutation.mutate();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Soporte e incidencias"
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <p className="text-sm text-slate-600">
          Tu reporte llega al equipo de la plataforma. Incluye el máximo detalle posible para
          agilizar la revisión.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategory(c.id)}
              className={`text-left rounded-xl border px-3 py-2.5 text-sm transition-colors ${
                category === c.id
                  ? 'border-slate-900 bg-slate-900 text-white shadow-md'
                  : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300'
              }`}
            >
              <span className="font-semibold block">{c.label}</span>
              <span
                className={`text-xs mt-0.5 block ${category === c.id ? 'text-slate-300' : 'text-slate-500'}`}
              >
                {c.hint}
              </span>
            </button>
          ))}
        </div>

        {metaLoading ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">Título *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                  placeholder="Resumen breve del problema"
                  maxLength={200}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Módulo afectado *
                </label>
                <select
                  value={module}
                  onChange={(e) => setModule(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
                >
                  {moduleOptions.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Severidad *
                </label>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as typeof severity)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
                >
                  <option value="LOW">Baja</option>
                  <option value="MEDIUM">Media</option>
                  <option value="HIGH">Alta</option>
                  <option value="CRITICAL">Crítica</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Descripción *
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-400"
                  placeholder="Qué ocurrió, qué esperabas y cualquier mensaje de error visible"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Pasos para reproducir (opcional)
                </label>
                <textarea
                  value={reproductionSteps}
                  onChange={(e) => setReproductionSteps(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="1. Ir a… 2. Clic en…"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Captura o adjunto (opcional)
                </label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium"
                />
                <p className="text-xs text-slate-500 mt-1">Máx. 5 MB — imágenes o PDF.</p>
              </div>
              <details className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2">
                <summary className="text-sm font-medium text-slate-700 cursor-pointer">
                  Detalles técnicos (opcional)
                </summary>
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Mensaje de error</label>
                    <input
                      type="text"
                      value={errorMessage}
                      onChange={(e) => setErrorMessage(e.target.value)}
                      className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Stack trace</label>
                    <textarea
                      value={stackTrace}
                      onChange={(e) => setStackTrace(e.target.value)}
                      rows={3}
                      className="w-full rounded border border-slate-200 px-2 py-1.5 font-mono text-[11px]"
                    />
                  </div>
                </div>
              </details>
            </div>
          </>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={createMutation.isLoading || metaLoading}
            className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {createMutation.isLoading ? (
              <>
                <LoadingSpinner size="sm" color="white" />
                Enviando…
              </>
            ) : (
              'Enviar reporte'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default IncidentReportModal;
